import * as React from 'react';
import {
  Dispatch,
  forwardRef,
  Fragment,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { WhatInputType } from 'what-input';
import { RegisterOptions, UseFormReturn } from 'react-hook-form';
import { idGenerator } from '../../../helpers/idGenerator';
import { ErrorRender } from '../../containers/ErrorRender';
import classNames from 'classnames';
import inputClasses from '../form.module.scss';
import { ListItem } from '../../actions/ListItem';
import classes from './Select.module.scss';
import { ListItemContainer } from '../../containers/ListItemContainer';
import { ListSectionTitle } from '../../actions/ListItem/ListSectionTitle';
import { FormElementValueType } from '../../../models/basic-objects';
import pluralize from 'pluralize';
import { ErrorMessageSeverity } from '../../../constants/form';
import { OutsideClickWrapper } from '../../actions/OutsideClickWrapper';
import { DropdownWrapper } from '../../containers/DropdownWrapper';

export type SelectItem = {
  label: string;
  value: FormElementValueType;
};

export type SelectDropdownTemplateProps = SelectViewProps;
export type SelectValueTemplate = (args: SelectViewProps, globalClasses: string) => JSX.Element;
export type SelectItemTemplate = (args: SelectViewProps, index: number, item: SelectItem) => JSX.Element;
export type SelectDropdownTemplate = (args: SelectDropdownTemplateProps, listDropdownClasses: string) => JSX.Element;
export type ClickOutsideHandlerFunction = (args: SelectViewProps) => void;

export type MultiSelectProps =
  | { multiple?: false; visibleMultipleValues: never }
  | { multiple: true; visibleMultipleValues?: number };

export type InputErrorMessageSeverityType =
  | { errorMessage?: string | JSX.Element; errorSeverity?: never }
  | { errorMessage: string | JSX.Element; errorSeverity: ErrorMessageSeverity };

export type SelectViewProps = SelectViewBaseProps & MultiSelectProps & InputErrorMessageSeverityType;

export type SelectViewBaseProps = {
  //
  chooseLabel: string;
  clickOutsideHandler?: ClickOutsideHandlerFunction;
  disabled?: boolean;
  dropdownContainerHeightType: 'desktop' | 'mobile';
  dropdownSide?: 'left' | 'right';
  dropdownTemplate?: SelectDropdownTemplate;
  expanded: boolean;
  hasOptionLabels: boolean;
  hideDefaultSelectOption?: boolean; // this prop is used to remove '- Select -' option form the dropdown list of available values
  id?: string;
  inlinedLabel?: boolean;
  intentType: WhatInputType;
  items: (SelectItem | string)[];
  itemTemplate?: SelectItemTemplate;
  label: string | undefined;
  name: string;
  optGroupTuples: [string, SelectItem[]][];
  readonly?: boolean;
  rules: RegisterOptions;
  selectedValue?: FormElementValueType;
  selectedLabel: string | string[];
  setExpanded: Dispatch<SetStateAction<boolean>>;
  size: 'large' | 'medium' | 'small';
  type?: 'light' | 'dark';
  useFormInstance: UseFormReturn;
  updateValue: (value: FormElementValueType) => void;
  valueTemplate?: SelectValueTemplate;
  wrapper?: boolean;
  emptyListItemTemplate?: JSX.Element;
  dropDownZIndex?: string;
};

// max number of visible items in the menu
const MAX_ITEMS_MOBILE = 4;
const MAX_ITEMS_DESKTOP = 6;

export const SelectView = forwardRef<HTMLDivElement, SelectViewProps>((props: SelectViewProps, ref): JSX.Element => {
  const {
    useFormInstance,
    name,
    disabled,
    readonly,
    id,
    size,
    items,
    expanded,
    dropdownContainerHeightType,
    intentType
  } = props;
  const inputID = useMemo(() => id ?? idGenerator(name), [id, name]);
  const localRef = useRef<HTMLSelectElement | HTMLInputElement | null>(null);
  const register = useFormInstance.register(name, disabled ? {} : props.rules);
  const inlinedLabel = size === 'large' && props.inlinedLabel;
  const type = props.type || 'light';
  const {
    formState: { errors },
    clearErrors
  } = useFormInstance;
  const [, rerender] = useState({});
  const portalElementRef = useRef<HTMLDivElement | null>(null);
  // This has to be inside view, due to forwarding ref limitations
  const dropDownRef = useRef<HTMLDivElement>(null);

  const globalClasses = classNames(inputClasses[size], inputClasses.dropdownArrow, {
    [inputClasses.FormSelects]: type === 'light',
    [inputClasses.FormSelectsDark]: type === 'dark',
    [inputClasses.largeInnerInput]: inlinedLabel,
    [inputClasses.FormSelectsErrorClass]:
      (!!errors && errors.hasOwnProperty(name)) || props.errorSeverity === ErrorMessageSeverity.error,
    [inputClasses.FormSelectsWarningClass]: props.errorSeverity === ErrorMessageSeverity.warning,
    [inputClasses.FormSelectsFocus]: expanded,
    [inputClasses.FormSelectsDisabled]: disabled || readonly
  });

  const templateSelectNativeClasses = classNames(globalClasses, inputClasses.FormSelectsTransparent);

  const errorClasses = classNames(
    inputClasses.HelpTextErrorClass,
    inputClasses.HelpText,
    inlinedLabel ? inputClasses.HelpTextInlined : null
  );

  const errorMessageClasses = classNames(inputClasses.HelpText, inlinedLabel ? inputClasses.HelpTextInlined : null, {
    [inputClasses.HelpTextErrorClass]: props.errorSeverity === ErrorMessageSeverity.error,
    [inputClasses.HelpTextWarningClass]: props.errorSeverity === ErrorMessageSeverity.warning
  });
  const labelClasses = classNames({
    [inputClasses.Label]: true,
    [inputClasses.LabelDark]: type === 'dark',
    [inputClasses.LabelInner]: inlinedLabel,
    [inputClasses.dropdownArrow]: inlinedLabel,
    [inputClasses.LabelDisabled]: disabled
  });

  const listDropdownClasses = classNames({
    [classes.SelectList]: true,
    [classes.SelectListRight]: props.dropdownSide === 'right',
    [classes.SelectListLeft]: props.dropdownSide === 'left'
  });

  const isSelectedItem = useCallback(
    (value: FormElementValueType) => {
      if (typeof props.selectedValue !== 'object') {
        return props.selectedValue === value;
      }
      return (props.selectedValue as readonly string[])?.includes(value as string);
    },
    [props.selectedValue]
  );

  const getSelectedLabel = useCallback(() => {
    if (typeof props.selectedLabel === 'string') {
      return props.selectedLabel;
    }
    return props.visibleMultipleValues && (props.selectedLabel as string[]).length > props.visibleMultipleValues
      ? `${(props.selectedLabel as string[]).length} ${pluralize('item', props.visibleMultipleValues)}`
      : (props.selectedLabel as string[]).join(', ');
  }, [props.selectedLabel, props.visibleMultipleValues]);

  const clickOnOutsideHandler = () => {
    if (props.clickOutsideHandler && expanded) {
      props.clickOutsideHandler(props);
    } else {
      if (expanded) {
        useFormInstance.trigger(name).then(result => {
          if (!result) {
            rerender({});
          }
        });
        props.setExpanded(false);
      }
    }
  };

  useEffect(() => {
    if (readonly || disabled) {
      clearErrors(name);
      rerender({});
    }
  }, [readonly, disabled, name, clearErrors]);

  useEffect(() => {
    if (expanded && intentType !== 'touch' && portalElementRef.current) {
      const selectedItem = portalElementRef.current.querySelector('li[data-selected]');
      const totalListItems = portalElementRef.current.querySelectorAll('li').length;

      const maxItems = dropdownContainerHeightType === 'mobile' ? MAX_ITEMS_MOBILE : MAX_ITEMS_DESKTOP;

      if (selectedItem && totalListItems > maxItems) {
        // ^^ if we have more than 4 li elements, scroller is there
        selectedItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [expanded, intentType, dropdownContainerHeightType]);

  const label = props.label ? (
    <label className={labelClasses} id={`${inputID}-label`}>
      {props.label}
    </label>
  ) : null;

  const optionsTouch = props.hasOptionLabels
    ? props.optGroupTuples.map((chunk, chunkIndex) => {
        const buffer = chunk[1].map((item, itemIndex) => (
          <option key={itemIndex} value={item.value} {...(disabled ? { disabled: true } : null)}>
            {item.label}
          </option>
        ));
        return (
          <optgroup label={chunk[0]} key={chunkIndex}>
            {buffer}
          </optgroup>
        );
      })
    : (items as SelectItem[]).map((item, index) => (
        <option key={index} value={item.value} {...(disabled ? { disabled: true } : null)}>
          {item.label}
        </option>
      ));

  const optionsMouse = items.map((item, index) => {
    if (typeof item === 'string') {
      return (
        <ListSectionTitle type={'li'} key={index}>
          {item}
        </ListSectionTitle>
      );
    } else {
      return props.itemTemplate ? (
        props.itemTemplate(props, index, item)
      ) : (
        <ListItem
          unsetWidth={!!props.dropdownSide}
          key={index}
          role={'option'}
          aria-label={item.label}
          variation={'regular'}
          type={'li'}
          selected={isSelectedItem(item.value)}
          action={e => {
            // Stop propagation as this may be nested inside another list / modal which we don't want to also have
            // actions act upon.
            e?.stopPropagation();
            props.updateValue(item.value);
          }}
          disabled={!!disabled || !!readonly}>
          {item.label}
        </ListItem>
      );
    }
  });

  const portalDropdown = (
    <DropdownWrapper
      expanded={expanded}
      portalWrapperRef={dropDownRef}
      id={`portal-dropdown-${inputID}`}
      baseZIndex={props.dropDownZIndex}>
      {props.dropdownTemplate ? (
        props.dropdownTemplate({ ...props }, listDropdownClasses)
      ) : (
        <div className={listDropdownClasses}>
          <ListItemContainer setExpanded={props.setExpanded} type={'ul'} expanded={expanded}>
            {items.length > 0 ? optionsMouse : props.emptyListItemTemplate}
            {props.selectedValue || props.hideDefaultSelectOption ? null : (
              <ListItem
                unsetWidth={!!props.dropdownSide}
                role={'option'}
                aria-label={props.chooseLabel}
                variation={'regular'}
                type={'li'}
                selected={false}
                action={() => {
                  const resetValue = props.multiple ? [] : undefined;
                  props.updateValue(resetValue);
                }}
                disabled={!!readonly || !!disabled}>
                {props.chooseLabel}
              </ListItem>
            )}
          </ListItemContainer>
        </div>
      )}
    </DropdownWrapper>
  );

  const elem =
    props.intentType === 'touch' ? (
      <>
        <select
          id={inputID}
          className={props.valueTemplate ? templateSelectNativeClasses : globalClasses}
          disabled={disabled}
          aria-readonly={readonly}
          name={register.name}
          ref={ref => {
            localRef.current = ref;
            register.ref(localRef.current);
          }}
          onBlur={register.onBlur}
          onChange={e => {
            register
              .onChange(e)
              .then(() => {
                useFormInstance
                  .trigger(register.name)
                  .then(() => {
                    props.updateValue(e.target.value);
                  })
                  .catch(() => {
                    // prevents runtime error
                  });
              })
              .catch(() => {
                // prevents runtime error
              });
          }}
          {...(props.multiple ? { multiple: true, size: 4 } : {})}>
          {optionsTouch}
          {props.selectedValue ? null : (
            <option value={''} {...(disabled || readonly ? { disabled: true } : null)}>
              {props.chooseLabel}
            </option>
          )}
        </select>
        {props.valueTemplate ? props.valueTemplate(props, globalClasses) : null}
      </>
    ) : (
      <OutsideClickWrapper
        onClickOutside={clickOnOutsideHandler}
        onFocusOutside={clickOnOutsideHandler}
        ref={dropDownRef}>
        <div className={'show-for-sr'}>
          <input
            id={inputID}
            ref={ref => {
              localRef.current = ref;
              register.ref(localRef.current);
            }}
            aria-labelledby={`${inputID}-label`}
            onChange={register.onChange}
            onBlur={register.onBlur}
            type={'hidden'}
            readOnly={true}
            role={'listbox'}
            name={register.name}
            aria-expanded={expanded}
          />
          <label id={`${inputID}-label`}>{register.name}</label>
        </div>
        <div
          onClick={e => {
            // Stop propagation as this may be nested inside another list / modal which we don't want to also have
            // actions act upon.
            e.stopPropagation();
            if (disabled || readonly) return;
            props.setExpanded(!expanded);
          }}>
          {props.valueTemplate ? (
            props.valueTemplate(props, globalClasses)
          ) : (
            <div className={globalClasses}>{getSelectedLabel()}</div>
          )}
        </div>
        {portalDropdown}
      </OutsideClickWrapper>
    );

  let buffer = (
    <>
      <div className={'relative'} ref={ref}>
        {label}
        {elem}
      </div>
      {errors && <ErrorRender className={errorClasses} errors={useFormInstance.formState.errors} name={name} />}
      {props.errorMessage
        ? typeof props.errorMessage === 'string'
          ? props.errorMessage.split(/\r?\n/).map((value, index) => (
              <p key={index} className={errorMessageClasses}>
                {value}
              </p>
            ))
          : props.errorMessage
        : null}
    </>
  );

  if (props.wrapper) {
    buffer = <div className={'input-wrapper'}>{buffer}</div>;
  }

  return <Fragment>{buffer}</Fragment>;
});

SelectView.displayName = 'SelectView';
